import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Check, SkipForward, MousePointerClick, Play } from "lucide-react";

/**
 * Fase 38 — Onboarding operativo medico (click-by-click, route-aware).
 *
 * Ogni step ha type:
 *   - "explain"   : solo evidenzia + spiega. Avanti sempre abilitato.
 *   - "click"     : evidenzia un target e attende che il medico clicchi DAVVERO.
 *                   Avanti disabilitato finché il click non è avvenuto (o cambio di path/selector atteso).
 *   - "route"     : richiede che l'utente arrivi a una path specifica (es. dopo il click su nav).
 *
 * Persistenza:
 *   - Server: /api/medico/tour  (completed/skipped)
 *   - Client (localStorage): step corrente e flag "welcome_shown" per medico.
 *
 * Il provider ascolta i cambi di route e ritenta il rendering del target per
 * pochi secondi prima di offrire "Vai alla pagina corretta" / "Salta questo passo".
 */

// -------- Step definitions --------
// path      : path esatta o array di path accettate (regex-like: se inizia con "/", match prefix)
// target    : query selector (data-testid preferito)
// waitForPath  : dopo l'azione, avanza solo quando la path diventa questa
// waitForSelector : dopo l'azione, avanza quando il selector compare in DOM
// requiresMobileMenu : su mobile apri il menu prima di cercare il target
export const MEDICO_TOUR_STEPS = [
  // A) Dashboard / Visite
  {
    id: "welcome-visite",
    path: "/visite",
    target: '[data-testid="page-title"]',
    title: "Benvenuto in Prime Vision",
    body: "Questa è l'area Visite: la lista ambulatoriale di OGGI (in attesa, in corso, completate). Lo storico completo è in Pazienti → Tutte le visite. Ti guido in ~5 minuti attraverso le funzioni principali.",
    type: "explain",
  },
  {
    id: "search-visite",
    path: "/visite",
    target: '[data-testid="today-status-filters"]',
    title: "Stato delle visite di oggi",
    body: "Filtra la lista di oggi per stato: in attesa, in corso/bozza o completate. Ogni riga mostra orario, paziente, tipologia e sede.",
    type: "explain",
  },
  {
    id: "toggle-filters",
    target: '[data-testid="nav-pazienti"], [data-testid="mnav-pazienti"]',
    title: "Pazienti e storico completo",
    body: "Nella sezione Pazienti trovi le anagrafiche con profilo completo (tag, consensi, appuntamenti, storico) e il tab \"Tutte le visite\" con ricerca e filtri avanzati.",
    type: "explain",
    optional: true,
  },
  {
    id: "click-new-visit",
    path: "/visite",
    target: '[data-testid="open-new-visit"]',
    title: "Crea una nuova visita",
    body: "Clicca qui per aprire il modale di creazione. Da lì scegli paziente esistente (per CF/email) o nuovo, e compili le anagrafiche.",
    type: "click",
    waitForSelector: '[data-testid="new-visit-modal"]',
  },
  {
    id: "explain-new-visit-modal",
    path: "/visite",
    target: '[data-testid="new-visit-modal"]',
    title: "Anagrafica paziente & servizio",
    body: "Compila nome, cognome, data nascita, CF, contatti (per referto via email/SMS). Scegli tipo di prestazione e sede. Il consenso privacy contatti è obbligatorio per i promemoria.",
    type: "explain",
    onEnter: (ctx) => {
      // Se il modale non è aperto (utente ha cliccato "Salta"), non forzarlo.
      // Il tour continua comunque.
    },
    onLeave: (ctx) => {
      // Chiudi il modale prima di andare all'agenda
      try { document.querySelector('[data-testid="new-visit-modal"]')?.parentElement?.click(); } catch { /* noop */ }
    },
  },

  // B) Agenda
  {
    id: "click-nav-agenda",
    target: '[data-testid="nav-agenda"], [data-testid="mnav-agenda"]',
    title: "Vai all'Agenda",
    body: "Clicca la voce Agenda nel menu laterale (o nel menu mobile) per pianificare gli appuntamenti.",
    type: "click",
    waitForPath: "/agenda",
    requiresMobileMenu: true,
  },
  {
    id: "explain-agenda-calendar",
    path: "/agenda",
    target: '[data-testid="calendar-legend"]',
    title: "Vista Agenda",
    body: "Visualizzi visite programmate del giorno con colore sede. Le visite già chiuse hanno colore differente. Puoi filtrare per sede/data/paziente.",
    type: "explain",
  },
  {
    id: "click-new-appt",
    path: "/agenda",
    target: '[data-testid="open-new-appt"]',
    title: "Nuovo appuntamento",
    body: "Clicca qui per aprire il modale. Da lì scegli data, ora, durata, sede, prestazione, paziente e (se autorizzato) attivi i promemoria automatici.",
    type: "click",
    waitForSelector: '[data-testid="new-appt-modal"]',
  },
  {
    id: "explain-appt-modal",
    path: "/agenda",
    target: '[data-testid="new-appt-modal"]',
    title: "Sede, prestazione, promemoria",
    body: "La sede assegna il colore alla riga in agenda. Il promemoria (email/SMS) si attiva se il paziente ha consenso ai contatti.",
    type: "explain",
    onLeave: () => {
      // Non lasciamo il modale aperto
      try { document.querySelector('[data-testid="new-appt-modal"]')?.parentElement?.click(); } catch { /* noop */ }
    },
  },

  // C) Cartella clinica (Visita)
  // C) Sedi (Fase 38B)
  {
    id: "click-nav-sedi",
    target: '[data-testid="nav-sedi"], [data-testid="mnav-sedi"]',
    title: "Vai alle Sedi",
    body: "Clicca la voce Sedi per gestire gli studi/location dove ricevi.",
    type: "click",
    waitForPath: "/sedi",
    requiresMobileMenu: true,
  },
  {
    id: "explain-sedi",
    path: "/sedi",
    target: '[data-testid="page-title"]',
    title: "Sedi / Studi",
    body: "Ogni sede può avere nome, indirizzo e un colore identificativo. Il colore viene mostrato in Agenda e sulle visite per riconoscere subito dove stai lavorando. Puoi arrivare fino a 5 sedi.",
    type: "explain",
  },

  // D) Ricerca tag (Fase 38B)
  {
    id: "click-nav-ricerca-tag",
    target: '[data-testid="nav-ricerca-tag"], [data-testid="mnav-ricerca-tag"]',
    title: "Vai alla Ricerca tag",
    body: "Clicca Ricerca tag per trovare pazienti e visite tramite tag persistenti.",
    type: "click",
    waitForPath: "/ricerca-tag",
    requiresMobileMenu: true,
    optional: true,
  },
  {
    id: "explain-ricerca-tag",
    path: "/ricerca-tag",
    target: '[data-testid="page-title"]',
    title: "Ricerca per tag clinici",
    body: "Cerca pazienti e visite per tag persistenti (#glaucoma, #pterigio, #cataratta, ecc.). Utile per follow-up mirati, statistiche di studio, richiami stagionali.",
    type: "explain",
    optional: true,
  },

  // E) Automazioni (Fase 38B)
  {
    id: "click-nav-automazioni",
    target: '[data-testid="nav-automazioni"], [data-testid="mnav-automazioni"]',
    title: "Vai alle Automazioni",
    body: "Clicca Automazioni per configurare promemoria e conferme automatiche verso i pazienti.",
    type: "click",
    waitForPath: "/automazioni",
    requiresMobileMenu: true,
    optional: true,
  },
  {
    id: "explain-automazioni",
    path: "/automazioni",
    target: '[data-testid="page-title"]',
    title: "Automazioni Brevo",
    body: "Configura promemoria appuntamento (email/SMS via Brevo), conferme paziente, richiami e messaggi personalizzati. Ogni invio è tracciato con esito reale (delivered, failed, opened).",
    type: "explain",
    optional: true,
  },

  {
    id: "back-to-visite",
    target: '[data-testid="nav-visite"], [data-testid="mnav-visite"]',
    title: "Torna a Visite",
    body: "Clicca Visite per aprire una bozza esistente e vedere la cartella clinica.",
    type: "click",
    waitForPath: "/visite",
    requiresMobileMenu: true,
  },
  {
    id: "explain-cartella",
    path: "/visite",
    title: "Cartella clinica",
    body: "Aprendo una visita accedi alla cartella: intestazione paziente, consenso, anamnesi master (persistente) e moduli clinici (visus, prescrizione lenti, IOP mmHg, SA, FO, diagnostica opzionale). Le sezioni OD/OS supportano compilazione speculare.",
    type: "explain",
  },
  {
    id: "explain-import-prev",
    path: "/visite",
    title: "Importa da ultima visita",
    body: 'Dentro una cartella troverai il pulsante "Importa tutto da ultima visita": copia visus, tono, segmento anteriore, fundus, diagnosi e terapia dall\'ultima visita chiusa dello stesso paziente. L\'anamnesi master resta intatta.',
    type: "explain",
  },

  // D) REC IA
  {
    id: "explain-rec-ia",
    path: "/visite",
    title: "REC IA & modalità testo",
    body: 'Nella cartella clinica troverai in basso a sinistra il pulsante REC IA (rosso) e "Modalità testo". REC IA prima TESTA il microfono e attiva l\'elaborazione SOLO se rileva un segnale audio reale. NON chiediamo il microfono adesso — lo farà quando premi REC volutamente.',
    type: "explain",
  },
  {
    id: "explain-ai-suggerimenti",
    path: "/visite",
    title: "Diagnosi, terapia, protocollo IA",
    body: "L'IA propone diagnosi con compatibilità 0-100%, terapia farmacologica coerente e protocollo comportamentale personalizzabile. Tu resti responsabile: valida, modifica o rifiuta ogni proposta.",
    type: "explain",
  },

  // E) Referto
  {
    id: "explain-referto",
    path: "/visite",
    title: "Referto PDF, email, SMS",
    body: 'Puoi refertare, modificare entro 1 ora dalla chiusura, generare PDF con firma e logo, e inviarlo via email o SMS. Ogni consegna è tracciata a audit log.',
    type: "explain",
  },

  // G) Impostazioni
  {
    id: "click-nav-settings",
    target: '[data-testid="nav-impostazioni"], [data-testid="mnav-impostazioni"]',
    title: "Vai in Impostazioni",
    body: "Clicca Impostazioni per personalizzare firma, referti base, modelli visita, protocolli, sedi e colori.",
    type: "click",
    waitForPath: "/impostazioni",
    requiresMobileMenu: true,
  },
  {
    id: "explain-settings",
    path: "/impostazioni",
    title: "Personalizzazione ambulatorio",
    body: 'Qui configuri: (1) referti base "nella norma", (2) modelli per tipologia visita, (3) protocolli preimpostati per patologia/tag, (4) firma e numero ordine, (5) sedi con colori, (6) feedback verso Prime Vision.',
    type: "explain",
  },

  // J) Fine
  {
    id: "end",
    title: "Guida completata",
    body: 'Puoi riaprirla in qualsiasi momento cliccando "Rivedi guida" nel menu laterale. Buon lavoro!',
    type: "explain",
  },
];

// -------- Helpers --------
const isPathMatch = (currentPath, expected) => {
  if (!expected) return true;
  if (Array.isArray(expected)) return expected.some((p) => isPathMatch(currentPath, p));
  // Prefix match: /visite matches /visite/ and /visite/xxx
  return currentPath === expected || currentPath.startsWith(expected + "/") || currentPath.startsWith(expected + "?");
};

const findTarget = (selector) => {
  if (!selector) return null;
  try { return document.querySelector(selector); } catch { return null; }
};

// Attende che un selector compaia (retry ogni 200ms fino a maxMs)
const waitForSelector = (selector, maxMs = 8000) =>
  new Promise((resolve) => {
    if (!selector) { resolve(null); return; }
    const started = Date.now();
    const check = () => {
      const el = findTarget(selector);
      if (el) { resolve(el); return; }
      if (Date.now() - started >= maxMs) { resolve(null); return; }
      setTimeout(check, 200);
    };
    check();
  });

// -------- Main component --------
export default function MedicoTour({ open, steps, userId, onFinish, onSkip, onClose, onOpenMobileMenu }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false); // modale "Ti guido io in 5 minuti"
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(false); // per step click/route
  const targetElRef = useRef(null);

  const stepKey = `pv-tour-step-${userId || "anonymous"}`;
  const welcomeKey = `pv-tour-welcome-${userId || "anonymous"}`;

  const step = steps?.[idx] || null;
  const isLast = idx === (steps?.length || 0) - 1;
  const isFirst = idx === 0;

  // Restore step corrente da localStorage all'apertura del tour
  useEffect(() => {
    if (!open) return;
    try {
      const saved = parseInt(window.localStorage.getItem(stepKey) || "0", 10);
      if (!Number.isNaN(saved) && saved >= 0 && saved < (steps?.length || 0)) {
        setIdx(saved);
      } else {
        setIdx(0);
      }
    } catch { setIdx(0); }
    // Mostra welcome solo alla prima apertura dell'account (non ai riavvii successivi)
    try {
      const shown = window.localStorage.getItem(welcomeKey) === "1";
      setShowWelcome(!shown);
    } catch { setShowWelcome(true); }
    setActionCompleted(false);
  }, [open, stepKey, welcomeKey, steps]);

  // Persist step ad ogni cambio
  useEffect(() => {
    if (!open) return;
    try { window.localStorage.setItem(stepKey, String(idx)); } catch { /* noop */ }
    setActionCompleted(false);
    setTargetMissing(false);
  }, [idx, open, stepKey]);

  // Route-aware: se lo step ha una path attesa diversa dalla corrente, mostra hint
  const routeMatches = step ? isPathMatch(location.pathname, step.path) : true;

  // Fase 38B: click intercept DOCUMENT-level (delegated).
  // Evita il race del vecchio approccio (addEventListener su un el catturato via
  // querySelector, che diventa stale se il parent re-renderizza a causa dello scroll).
  // Il listener sta su document con capture:true e usa Event.target.closest(selector).
  useEffect(() => {
    if (!open || showWelcome) return;
    if (!step || step.type !== "click" || !step.target) return;

    let cancelled = false;

    // Se richiede menu mobile e sidebar non è visibile, apri
    if (step.requiresMobileMenu) {
      const sidebarNav = document.querySelector('[data-testid="sidebar-nav"]');
      const sidebarVisible = sidebarNav && sidebarNav.offsetParent !== null;
      if (!sidebarVisible && onOpenMobileMenu) {
        try { onOpenMobileMenu(); } catch { /* noop */ }
      }
    }

    const handler = (ev) => {
      if (cancelled) return;
      try {
        const clicked = ev.target && ev.target.closest ? ev.target.closest(step.target) : null;
        if (!clicked) return;
        // Il click è avvenuto sul (o dentro il) target atteso
        if (step.waitForSelector) {
          // MutationObserver lo gestirà (vedi effect sotto)
          return;
        }
        if (step.waitForPath) {
          // L'effect di route change lo gestirà
          return;
        }
        // Nessuna condizione post → il click stesso soddisfa
        setActionCompleted(true);
      } catch { /* noop */ }
    };
    document.addEventListener("click", handler, { capture: true });
    return () => {
      cancelled = true;
      try { document.removeEventListener("click", handler, { capture: true }); } catch { /* noop */ }
    };
  }, [open, showWelcome, step, idx, onOpenMobileMenu]);

  // Fase 38B: MutationObserver per waitForSelector.
  // Se lo step click ha waitForSelector, osserva il DOM e completa l'azione
  // quando l'elemento atteso compare (es. new-visit-modal, new-appt-modal).
  // Robusto contro re-mount del target originario.
  useEffect(() => {
    if (!open || showWelcome) return;
    if (!step || step.type !== "click" || !step.waitForSelector) return;
    // Se già presente all'ingresso dello step (raro ma possibile), completa subito
    if (findTarget(step.waitForSelector)) { setActionCompleted(true); return; }
    const observer = new MutationObserver(() => {
      if (findTarget(step.waitForSelector)) {
        setActionCompleted(true);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { try { observer.disconnect(); } catch { /* noop */ } };
  }, [open, showWelcome, step, idx]);

  // waitForPath: attende cambio route
  useEffect(() => {
    if (!open || showWelcome) return;
    if (!step || step.type !== "click" || !step.waitForPath) return;
    if (isPathMatch(location.pathname, step.waitForPath)) {
      setActionCompleted(true);
    }
  }, [open, showWelcome, location.pathname, step]);

  // Measure target rect (per spotlight/tooltip).
  // scrollIntoView è chiamato SOLO alla prima misura per step (evita loop
  // scroll → setRect → re-render → scrollIntoView → ...).
  const scrolledForStepRef = useRef(-1);
  const measure = useCallback(() => {
    if (!step) return;
    if (!step.target) { setRect(null); return; }
    if (!routeMatches) { setRect(null); return; }
    const el = findTarget(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      if (scrolledForStepRef.current !== idx) {
        scrolledForStepRef.current = idx;
        try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }); } catch { /* noop */ }
      }
      setTargetMissing(false);
    } else {
      setRect(null);
    }
  }, [step, routeMatches, idx]);

  // Ricerca target con retry (per gestire render lazy dei componenti)
  useEffect(() => {
    if (!open || showWelcome) return;
    if (!step) return;
    setTargetMissing(false);
    let cancelled = false;
    const started = Date.now();
    const maxMs = 4000;
    const tick = async () => {
      if (cancelled || !open) return;
      if (!step.target) { setRect(null); return; }
      const el = findTarget(step.target);
      if (el) { measure(); return; }
      if (Date.now() - started < maxMs) {
        setTimeout(tick, 200);
      } else {
        setTargetMissing(true);
        setRect(null);
      }
    };
    tick();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, showWelcome, idx, step, location.pathname, measure]);

  if (!open || !step) return null;

  const canGoNext =
    step.type === "explain" ||
    (step.type === "click" && actionCompleted) ||
    step.optional;

  const goNext = () => {
    if (isLast) { onFinish?.(); return; }
    if (typeof step.onLeave === "function") { try { step.onLeave(); } catch { /* noop */ } }
    setIdx((i) => Math.min(steps.length - 1, i + 1));
  };
  const goPrev = () => setIdx((i) => Math.max(0, i - 1));

  const skipStep = () => {
    // Salta questo passo, avanza al prossimo
    if (isLast) { onFinish?.(); return; }
    setIdx((i) => Math.min(steps.length - 1, i + 1));
  };

  const exitTour = () => { onClose?.(); };

  // -------- Welcome modal --------
  if (showWelcome) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-slate-900/70 flex items-center justify-center p-4" data-testid="medico-tour-welcome" role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-brand-azure/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-brand-azure" />
            </div>
            <h3 className="text-xl font-extrabold text-brand-navy">Ti guido io in 5 minuti</h3>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Ti mostro le funzioni principali di Prime Vision con brevi passi interattivi:
            crea una visita, apri l'agenda, personalizza le impostazioni. Puoi mettere in pausa o riaprire la guida quando vuoi da <b>Rivedi guida</b> nel menu.
          </p>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              data-testid="tour-welcome-skip"
              onClick={() => {
                try { window.localStorage.setItem(welcomeKey, "1"); } catch { /* noop */ }
                onSkip?.();
              }}
              className="text-xs font-bold text-slate-500 hover:text-slate-700 px-3 py-2 inline-flex items-center gap-1 rounded"
            >
              <SkipForward className="w-3.5 h-3.5" /> Salta
            </button>
            {idx > 0 && (
              <button
                type="button"
                data-testid="tour-welcome-resume"
                onClick={() => {
                  try { window.localStorage.setItem(welcomeKey, "1"); } catch { /* noop */ }
                  setShowWelcome(false);
                }}
                className="text-xs font-bold text-brand-navy border border-brand-navy hover:bg-brand-navy hover:text-white px-4 py-2 rounded inline-flex items-center gap-1"
              >
                Riprendi (passo {idx + 1})
              </button>
            )}
            <button
              type="button"
              data-testid="tour-welcome-start"
              onClick={() => {
                try { window.localStorage.setItem(welcomeKey, "1"); } catch { /* noop */ }
                setIdx(0);
                try { window.localStorage.setItem(stepKey, "0"); } catch { /* noop */ }
                setShowWelcome(false);
              }}
              className="text-xs font-bold text-white bg-brand-orange hover:bg-brand-orangeDark px-4 py-2 rounded inline-flex items-center gap-1"
            >
              <Play className="w-3.5 h-3.5" /> Inizia guida
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // -------- Tooltip position --------
  const tooltipStyle = (() => {
    const style = { maxWidth: 380, width: "min(92vw, 380px)" };
    if (!rect) {
      return { ...style, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const spaceRight = window.innerWidth - (rect.left + rect.width);
    if (spaceBelow > 220) {
      return { ...style, top: rect.top + rect.height + 12, left: Math.max(8, Math.min(rect.left, window.innerWidth - 400)) };
    }
    if (spaceRight > 400) {
      return { ...style, top: Math.max(8, rect.top), left: rect.left + rect.width + 12 };
    }
    return { ...style, top: Math.max(8, rect.top - 260), left: Math.max(8, Math.min(rect.left, window.innerWidth - 400)) };
  })();

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="medico-tour" aria-modal="true" role="dialog">
      {/* Overlay + spotlight sul target */}
      {rect ? (
        <div
          className={`absolute pointer-events-none ${step.type === "click" ? "animate-pulse" : ""}`}
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
            borderRadius: 12,
            border: `2px solid ${step.type === "click" ? "#f97316" : "#3b82f6"}`,
            transition: "all 200ms ease",
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/55 pointer-events-none" data-testid="tour-overlay" />
      )}

      {/* Tooltip card — pointer-events-auto per essere cliccabile pur essendo dentro root pointer-events-none */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 p-4 pointer-events-auto"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-brand-azure" data-testid="tour-step-index">
            Passo {idx + 1} di {steps.length}
          </div>
          <button
            type="button"
            aria-label="Esci dalla guida"
            onClick={exitTour}
            className="text-slate-400 hover:text-slate-700"
            data-testid="tour-close-btn"
            title="Esci dalla guida"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="font-extrabold text-brand-navy text-base mb-1" data-testid="tour-step-title">{step.title}</h3>
        <div className="text-sm text-slate-700 leading-relaxed mb-3" data-testid="tour-step-body">
          {step.body}
        </div>

        {/* Click required indicator */}
        {step.type === "click" && !actionCompleted && !targetMissing && routeMatches && (
          <div className="mb-3 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-center gap-1.5" data-testid="tour-click-required">
            <MousePointerClick className="w-3.5 h-3.5" /> Attendo che tu clicchi il pulsante evidenziato per continuare.
          </div>
        )}

        {/* Route mismatch → offri di navigare */}
        {step.path && !routeMatches && (
          <div className="mb-3 text-[11px] font-bold text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 flex flex-col gap-1.5" data-testid="tour-route-mismatch">
            <span>Sei su un'altra pagina. Vai a <b>{Array.isArray(step.path) ? step.path[0] : step.path}</b> per vedere l'elemento.</span>
            <button
              type="button"
              onClick={() => navigate(Array.isArray(step.path) ? step.path[0] : step.path)}
              className="self-start text-white bg-brand-navy hover:bg-brand-navyDark rounded px-2 py-1 text-[11px] font-bold"
              data-testid="tour-goto-path"
            >
              Vai alla pagina corretta
            </button>
          </div>
        )}

        {/* Target missing → offri skip */}
        {targetMissing && routeMatches && (
          <div className="mb-3 text-[11px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5" data-testid="tour-target-missing">
            L'elemento non è visibile in questa pagina. Puoi saltare questo passo.
          </div>
        )}

        {/* Progress dots */}
        <div className="flex items-center gap-1 mb-3" data-testid="tour-progress">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-brand-azure w-6" : i < idx ? "bg-brand-azure/60 w-1.5" : "bg-slate-300 w-1.5"}`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={exitTour}
              className="text-xs font-bold text-slate-500 hover:text-slate-700 px-2 py-1.5 inline-flex items-center gap-1"
              data-testid="tour-skip-btn"
              title="Esci dalla guida"
            >
              <X className="w-3.5 h-3.5" /> Esci
            </button>
            <button
              type="button"
              onClick={skipStep}
              className="text-xs font-bold text-slate-500 hover:text-slate-700 px-2 py-1.5 inline-flex items-center gap-1"
              data-testid="tour-skip-step-btn"
              title="Salta questo passo"
            >
              <SkipForward className="w-3.5 h-3.5" /> Salta passo
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isFirst}
              onClick={goPrev}
              className="text-xs font-bold text-brand-navy hover:bg-slate-100 disabled:opacity-40 px-3 py-1.5 rounded inline-flex items-center gap-1"
              data-testid="tour-prev-btn"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Indietro
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={onFinish}
                className="text-xs font-bold text-white bg-brand-azure hover:bg-brand-navy px-4 py-1.5 rounded inline-flex items-center gap-1"
                data-testid="tour-finish-btn"
              >
                <Check className="w-3.5 h-3.5" /> Fine
              </button>
            ) : (
              <button
                type="button"
                disabled={!canGoNext}
                onClick={goNext}
                className={`text-xs font-bold px-4 py-1.5 rounded inline-flex items-center gap-1 ${canGoNext ? "text-white bg-brand-azure hover:bg-brand-navy" : "text-slate-400 bg-slate-100 cursor-not-allowed"}`}
                data-testid="tour-next-btn"
                title={canGoNext ? "Vai al prossimo passo" : "Completa l'azione richiesta o clicca 'Salta passo'"}
              >
                Avanti <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
